import { useState, useId } from "react"
import { useNavigate } from "react-router-dom"
import {
  Mail,
  MessageSquare,
  ListTodo,
  StickyNote,
  Printer,
  Phone,
  Plus,
  Send,
  MoreHorizontal,
} from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useCreateTask } from "@/hooks/use-tasks"
import { useSendSms, useSmsAccounts } from "@/hooks/use-sms"
import { toast } from "sonner"

// ── Types ────────────────────────────────────────────────────────

export type EntityType = "Company" | "Contact"

export interface QuickActionsEntity {
  id: string
  entityType: EntityType
  name: string
  primaryEmail?: string | null
  primaryPhone?: string | null
}

interface QuickActionsProps {
  entity: QuickActionsEntity
  /** Callback to switch to the notes tab */
  onOpenNotes?: () => void
}

// ── Quick Task Dialog ────────────────────────────────────────────

function QuickTaskDialog({
  open,
  onOpenChange,
  entity,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  entity: QuickActionsEntity
}) {
  const createTaskMutation = useCreateTask()
  const [title, setTitle] = useState("")
  const [dueDate, setDueDate] = useState("")
  const [priority, setPriority] = useState<string>("normal")
  const titleId = useId()
  const dueDateId = useId()
  const priorityId = useId()

  const handleSubmit = async () => {
    if (!title.trim()) return

    const taskData: Record<string, unknown> = {
      title: title.trim(),
      priority,
      entity_links: [
        {
          entity_type: entity.entityType,
          entity_id: entity.id,
        },
      ],
    }

    if (dueDate) {
      taskData.due_date = dueDate
    }

    try {
      await createTaskMutation.mutateAsync(taskData)
      toast.success("Zadanie utworzone")
      setTitle("")
      setDueDate("")
      setPriority("normal")
      onOpenChange(false)
    } catch {
      toast.error("Nie udalo sie utworzyc zadania")
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey && title.trim()) {
      e.preventDefault()
      handleSubmit()
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Dodaj zadanie</DialogTitle>
          <DialogDescription className="text-xs">
            Powiazane z: {entity.name}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {/* Title */}
          <div>
            <label htmlFor={titleId} className="mb-1 block text-xs font-medium text-muted-foreground">
              Tytul *
            </label>
            <input
              id={titleId}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full rounded-md border border-input bg-transparent px-2.5 py-1.5 text-sm text-foreground outline-none focus:border-ring"
              placeholder="Nazwa zadania..."
              autoFocus
            />
          </div>

          {/* Due date */}
          <div>
            <label htmlFor={dueDateId} className="mb-1 block text-xs font-medium text-muted-foreground">
              Termin
            </label>
            <input
              id={dueDateId}
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="w-full rounded-md border border-input bg-transparent px-2.5 py-1.5 text-sm text-foreground outline-none focus:border-ring"
            />
          </div>

          {/* Priority */}
          <div>
            <label htmlFor={priorityId} className="mb-1 block text-xs font-medium text-muted-foreground">
              Priorytet
            </label>
            <select
              id={priorityId}
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="w-full rounded-md border border-input bg-transparent px-2.5 py-1.5 text-sm text-foreground outline-none focus:border-ring"
            >
              <option value="low">Niski</option>
              <option value="normal">Normalny</option>
              <option value="high">Wysoki</option>
              <option value="urgent">Pilny</option>
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            size="sm"
          >
            Anuluj
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createTaskMutation.isPending || !title.trim()}
            size="sm"
            className="gap-1.5"
          >
            <Plus size={13} />
            {createTaskMutation.isPending ? "Tworzenie..." : "Utworz zadanie"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Quick SMS Dialog ─────────────────────────────────────────────

function QuickSmsDialog({
  open,
  onOpenChange,
  entity,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  entity: QuickActionsEntity
}) {
  const { data: accountsData } = useSmsAccounts()
  const accounts = accountsData?.data || []
  const sendMutation = useSendSms()

  const [accountId, setAccountId] = useState("")
  const [toNumber, setToNumber] = useState(entity.primaryPhone || "")
  const [body, setBody] = useState("")
  const smsAccountId = useId()
  const smsNumberId = useId()
  const smsBodyId = useId()

  const charCount = body.length
  const partsCount = charCount <= 160 ? 1 : Math.ceil(charCount / 153)
  const charsLeft =
    partsCount === 1 ? 160 - charCount : 153 - (charCount % 153 || 153)

  const handleSend = async () => {
    const acctId = accountId || accounts[0]?.id
    if (!acctId || !toNumber || !body) return

    try {
      await sendMutation.mutateAsync({
        sms_account_id: acctId,
        to_number: toNumber,
        body,
        entity_type: entity.entityType,
        entity_id: entity.id,
      })
      toast.success("SMS wyslany")
      setBody("")
      onOpenChange(false)
    } catch {
      toast.error("Blad wysylania SMS")
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Wyslij SMS</DialogTitle>
          <DialogDescription className="text-xs">
            Do: {entity.name}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {/* Account */}
          {accounts.length > 1 && (
            <div>
              <label htmlFor={smsAccountId} className="mb-1 block text-xs font-medium text-muted-foreground">
                Konto SMS
              </label>
              <select
                id={smsAccountId}
                value={accountId || accounts[0]?.id || ""}
                onChange={(e) => setAccountId(e.target.value)}
                className="w-full rounded-md border border-input bg-transparent px-2.5 py-1.5 text-sm text-foreground outline-none"
              >
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} ({a.sender_name || a.provider})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* To number */}
          <div>
            <label htmlFor={smsNumberId} className="mb-1 block text-xs font-medium text-muted-foreground">
              Numer telefonu *
            </label>
            <input
              id={smsNumberId}
              value={toNumber}
              onChange={(e) => setToNumber(e.target.value)}
              className="w-full rounded-md border border-input bg-transparent px-2.5 py-1.5 text-sm text-foreground outline-none"
              placeholder="+48 123 456 789"
            />
          </div>

          {/* Body */}
          <div>
            <label htmlFor={smsBodyId} className="mb-1 block text-xs font-medium text-muted-foreground">
              Tresc wiadomosci *
            </label>
            <textarea
              id={smsBodyId}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="w-full min-h-[100px] rounded-md border border-input bg-transparent px-2.5 py-1.5 text-sm text-foreground outline-none resize-none"
              placeholder="Wpisz tresc SMS..."
              autoFocus
            />
            <div className="mt-1 flex items-center justify-end">
              <span
                className={`text-[10px] ${charCount > 160 ? "text-amber-500" : "text-muted-foreground"}`}
              >
                {charCount}/160 znakow &middot; {partsCount}{" "}
                {partsCount === 1
                  ? "czesc"
                  : partsCount < 5
                    ? "czesci"
                    : "czesci"}
                {charsLeft > 0 && ` \u00B7 ${charsLeft} zost.`}
              </span>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            size="sm"
          >
            Anuluj
          </Button>
          <Button
            onClick={handleSend}
            disabled={
              sendMutation.isPending ||
              !toNumber ||
              !body ||
              accounts.length === 0
            }
            size="sm"
            className="gap-1.5"
          >
            <Send size={13} />
            {sendMutation.isPending ? "Wysylanie..." : "Wyslij"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Action Button ────────────────────────────────────────────────

function ActionButton({
  icon: Icon,
  label,
  onClick,
  href,
}: {
  icon: React.ComponentType<{ size?: number }>
  label: string
  onClick?: () => void
  href?: string
}) {
  const button = (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {href ? (
            <a
              href={href}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-transparent text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              <Icon size={15} />
            </a>
          ) : (
            <button
              onClick={onClick}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-transparent text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              <Icon size={15} />
            </button>
          )}
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p>{label}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )

  return button
}

// ── Main QuickActions Component ──────────────────────────────────

export function QuickActions({ entity, onOpenNotes }: QuickActionsProps) {
  const navigate = useNavigate()
  const [taskDialogOpen, setTaskDialogOpen] = useState(false)
  const [smsDialogOpen, setSmsDialogOpen] = useState(false)

  const handleSendEmail = () => {
    const params = new URLSearchParams()
    if (entity.primaryEmail) params.set("to", entity.primaryEmail)
    params.set("entity_type", entity.entityType)
    params.set("entity_id", entity.id)
    navigate(`/mail?compose=new&${params.toString()}`)
  }

  const handlePrint = () => {
    window.print()
  }

  const handleAddNote = () => {
    onOpenNotes?.()
  }

  const handleAddDeal = () => {
    const params = new URLSearchParams()
    params.set("company_id", entity.id)
    navigate(`/deals/new?${params.toString()}`)
  }

  // Build actions list based on entity type
  const isCompany = entity.entityType === "Company"
  const isContact = entity.entityType === "Contact"

  interface ActionItem {
    icon: React.ComponentType<{ size?: number }>
    label: string
    onClick?: () => void
    href?: string
  }

  const actions: ActionItem[] = [
    {
      icon: Mail,
      label: "Wyslij email",
      onClick: handleSendEmail,
    },
    {
      icon: MessageSquare,
      label: "Wyslij SMS",
      onClick: () => setSmsDialogOpen(true),
    },
    {
      icon: ListTodo,
      label: "Dodaj zadanie",
      onClick: () => setTaskDialogOpen(true),
    },
    {
      icon: StickyNote,
      label: "Dodaj notatke",
      onClick: handleAddNote,
    },
  ]

  // Contact-specific: Call action
  if (isContact && entity.primaryPhone) {
    actions.push({
      icon: Phone,
      label: "Zadzwon",
      href: `tel:${entity.primaryPhone}`,
    })
  }

  // Company-specific: Add deal
  if (isCompany) {
    actions.push({
      icon: Plus,
      label: "Dodaj transakcje",
      onClick: handleAddDeal,
    })
  }

  // Print is always last
  actions.push({
    icon: Printer,
    label: "Drukuj",
    onClick: handlePrint,
  })

  return (
    <>
      {/* Desktop: inline icon buttons */}
      <div className="hidden md:flex items-center gap-0.5">
        {actions.map((action) => (
          <ActionButton
            key={action.label}
            icon={action.icon}
            label={action.label}
            onClick={action.onClick}
            href={action.href}
          />
        ))}
      </div>

      {/* Mobile: dropdown menu */}
      <div className="flex md:hidden">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-input text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground">
              <MoreHorizontal size={15} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {actions.map((action) => {
              const Icon = action.icon
              return (
                <DropdownMenuItem
                  key={action.label}
                  onClick={() => {
                    if (action.href) {
                      window.location.href = action.href
                    } else {
                      action.onClick?.()
                    }
                  }}
                >
                  <Icon size={14} />
                  <span>{action.label}</span>
                </DropdownMenuItem>
              )
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Dialogs */}
      <QuickTaskDialog
        open={taskDialogOpen}
        onOpenChange={setTaskDialogOpen}
        entity={entity}
      />
      <QuickSmsDialog
        open={smsDialogOpen}
        onOpenChange={setSmsDialogOpen}
        entity={entity}
      />
    </>
  )
}
